/**
 * WidgetRSP.js — Phidget Résolution de Problèmes (RSP)
 * Design System SEIZO : Sofia Pro / Nunito | #002335 | #00B0FF | #F4F7F9
 *
 * Vue "carte" (dashboard) : KPIs + bouton ouvrir
 * Vue "plein écran" : workflow 5 étapes
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useStores } from '../../../stores/Context';
import { createAxiosEntrepriseInstance } from '../../../axiosEntreprise';
import styles from './WidgetRSP.module.scss';
import {
    RSP_DEFAULT_DISPLAY,
    readDisplayPreferences,
    saveDisplayPreferences,
} from '../levelZeroDisplayPreferences';

// ── Breakpoints ───────────────────────────────────────────────────────────────
const getBp = (w, h) => {
    if (w < 200 || h < 120) return 'xs';
    if (w < 340 || h < 200) return 'sm';
    if (w < 500 || h < 300) return 'md';
    return 'lg';
};

// ── Icônes inline ─────────────────────────────────────────────────────────────
const IcoSettings = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
);

const IcoArrow = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
);

const IcoBulb = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21h6M12 3a6 6 0 00-3 11.22V17h6v-2.78A6 6 0 0012 3z"/>
        <path d="M9 17h6"/>
    </svg>
);

const IcoAlert = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const IcoCheck = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const IcoPlus = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const IcoTrash = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14H6L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4h6v2"/>
    </svg>
);

const IcoEdit = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);

const RSP_DISPLAY_OPTIONS = [
    {key: 'showKpis', label: 'Indicateurs de suivi', description: 'Total, problèmes en cours et résolus'},
    {key: 'showRecentProblems', label: 'Derniers problèmes', description: 'Liste des sujets ouverts sur les grandes cartes'},
    {key: 'showOpenCta', label: 'Accès au module', description: 'Lien d’ouverture sur les grandes cartes'},
];

const RspDisplaySettings = ({config, onSave, onClose}) => {
    const [localConfig, setLocalConfig] = useState(config);

    useEffect(() => setLocalConfig(config), [config]);

    return (
        <div className={styles.displaySettingsOverlay} onClick={onClose} role="presentation">
            <section className={styles.displaySettingsPanel} onClick={event => event.stopPropagation()} aria-label="Préférences d’affichage">
                <header>
                    <div>
                        <strong>Paramétrer l’affichage</strong>
                        <span>Ces préférences sont conservées pour ce phidget.</span>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Fermer">×</button>
                </header>
                <div className={styles.displaySettingsList}>
                    {RSP_DISPLAY_OPTIONS.map(option => (
                        <label key={option.key} className={styles.displaySettingsOption}>
                            <span>
                                <strong>{option.label}</strong>
                                <small>{option.description}</small>
                            </span>
                            <input
                                type="checkbox"
                                checked={Boolean(localConfig[option.key])}
                                onChange={() => setLocalConfig(current => ({...current, [option.key]: !current[option.key]}))}
                            />
                        </label>
                    ))}
                </div>
                <footer>
                    <button type="button" className={styles.displaySettingsCancel} onClick={onClose}>Annuler</button>
                    <button type="button" className={styles.displaySettingsSave} onClick={() => { onSave(localConfig); onClose(); }}>Enregistrer</button>
                </footer>
            </section>
        </div>
    );
};

// ── Statuts ───────────────────────────────────────────────────────────────────
const STATUTS = {
    ouvert:    { label: 'Ouvert',    color: '#00B0FF', bg: '#E8F7FF' },
    en_cours:  { label: 'En cours',  color: '#FF9436', bg: '#FFF4E8' },
    resolu:    { label: 'Résolu',    color: '#66BE63', bg: '#EBF7EB' },
    abandonne: { label: 'Abandonné', color: '#A8B0B3', bg: '#F4F7F9' },
};

// ── Étapes du workflow ────────────────────────────────────────────────────────
const ETAPES = [
    { n: 1, label: 'Définir',   desc: 'QQOQCCP' },
    { n: 2, label: 'Analyser',  desc: '6M / Ishikawa' },
    { n: 3, label: 'Résoudre',  desc: 'Solutions' },
    { n: 4, label: 'Décider',   desc: 'Matrice' },
    { n: 5, label: 'Valider',   desc: 'Clôture' },
];

// ── Catégories 6M ─────────────────────────────────────────────────────────────
const CATEGORIES_6M = [
    { id: 'main_oeuvre',  label: 'Main d\'œuvre', color: '#00B0FF', emoji: '👤' },
    { id: 'matiere',      label: 'Matière',        color: '#66BE63', emoji: '📦' },
    { id: 'methode',      label: 'Méthode',        color: '#FF9436', emoji: '📋' },
    { id: 'machine',      label: 'Machine',        color: '#F82121', emoji: '⚙️' },
    { id: 'milieu',       label: 'Milieu',         color: '#9B59B6', emoji: '🌍' },
    { id: 'management',   label: 'Management',     color: '#F8D541', emoji: '🎯' },
];

// ── Composant carte problème ──────────────────────────────────────────────────
const CarteProbleme = ({ probleme, onClick, onDelete }) => {
    const statut = STATUTS[probleme.statut] || STATUTS.ouvert;
    const etape = ETAPES.find(e => e.n === (probleme.etape_courante || 1));
    return (
        <div className={styles.carteProbleme} onClick={onClick}>
            <div className={styles.carteHeader}>
                <span className={styles.carteStatut} style={{ color: statut.color, background: statut.bg }}>
                    {statut.label}
                </span>
                <span className={styles.carteEtape}>Étape {etape?.n}/5 — {etape?.label}</span>
            </div>
            <div className={styles.carteTitre}>{probleme.titre}</div>
            {probleme.description && (
                <div className={styles.carteDesc}>{probleme.description.substring(0, 80)}{probleme.description.length > 80 ? '…' : ''}</div>
            )}
            <div className={styles.carteFooter}>
                <span className={styles.carteDate}>
                    {new Date(probleme.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                </span>
                <div className={styles.carteActions}>
                    <button className={styles.btnIcone} onClick={e => { e.stopPropagation(); onDelete(probleme.id); }} title="Supprimer">
                        <IcoTrash size={12}/>
                    </button>
                    <IcoArrow/>
                </div>
            </div>
        </div>
    );
};

// ── Barre de progression des étapes ──────────────────────────────────────────
const BarreEtapes = ({ etapeCourante, onClickEtape }) => (
    <div className={styles.barreEtapes}>
        {ETAPES.map((e, i) => {
            const done = e.n < etapeCourante;
            const active = e.n === etapeCourante;
            return (
                <React.Fragment key={e.n}>
                    <button
                        className={`${styles.etapeBtn} ${done ? styles.etapeDone : ''} ${active ? styles.etapeActive : ''}`}
                        onClick={() => onClickEtape(e.n)}
                    >
                        <span className={styles.etapeNum}>
                            {done ? <IcoCheck size={11} color="#fff"/> : e.n}
                        </span>
                        <span className={styles.etapeLabel}>{e.label}</span>
                        <span className={styles.etapeDesc}>{e.desc}</span>
                    </button>
                    {i < ETAPES.length - 1 && (
                        <div className={`${styles.etapeConnecteur} ${done ? styles.etapeConnecteurDone : ''}`}/>
                    )}
                </React.Fragment>
            );
        })}
    </div>
);

// ── Étape 1 : QQOQCCP ────────────────────────────────────────────────────────
const Etape1QQOQCCP = observer(({ store }) => {
    const p = store.problemeActif;
    const [form, setForm] = useState({
        qui: p?.qui || '', quoi: p?.quoi || '', ou: p?.ou || '',
        quand: p?.quand || '', comment: p?.comment || '',
        combien: p?.combien || '', pourquoi: p?.pourquoi || '',
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await store.updateProbleme(p.id, { ...form, etape_courante: 1, statut: 'en_cours' });
        } finally {
            setSaving(false);
        }
    };

    const CHAMPS = [
        { key: 'qui',      label: 'Qui ?',      placeholder: 'Qui est concerné ?' },
        { key: 'quoi',     label: 'Quoi ?',     placeholder: 'Quel est le problème ?' },
        { key: 'ou',       label: 'Où ?',       placeholder: 'Où se produit-il ?' },
        { key: 'quand',    label: 'Quand ?',    placeholder: 'Quand est-il apparu ?' },
        { key: 'comment',  label: 'Comment ?',  placeholder: 'Comment se manifeste-t-il ?' },
        { key: 'combien',  label: 'Combien ?',  placeholder: 'Quelle est l\'ampleur ?' },
        { key: 'pourquoi', label: 'Pourquoi ?', placeholder: 'Pourquoi est-ce un problème ?' },
    ];

    return (
        <div className={styles.etapeContent}>
            <div className={styles.etapeTitre}>
                <IcoAlert size={18} color="#00B0FF"/>
                <span>Définir le problème — QQOQCCP</span>
            </div>
            <div className={styles.qqoqccpGrid}>
                {CHAMPS.map(c => (
                    <div key={c.key} className={styles.qqoqccpChamp}>
                        <label>{c.label}</label>
                        <textarea
                            value={form[c.key]}
                            onChange={e => setForm({ ...form, [c.key]: e.target.value })}
                            placeholder={c.placeholder}
                            rows={2}
                        />
                    </div>
                ))}
            </div>
            <div className={styles.etapeFooter}>
                <button className={styles.btnSave} onClick={handleSave} disabled={saving}>
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button className={styles.btnNext} onClick={() => store.advanceEtape(2)}>
                    Étape suivante <IcoArrow/>
                </button>
            </div>
        </div>
    );
});

// ── Étape 2 : Causes 6M / Ishikawa ───────────────────────────────────────────
const Etape2Causes = observer(({ store }) => {
    const p = store.problemeActif;
    const [nouvelleCategorie, setNouvelleCategorie] = useState(CATEGORIES_6M[0].id);
    const [nouvelleDesc, setNouvelleDesc] = useState('');
    const [indicatorTargets, setIndicatorTargets] = useState([]);
    const [indicatorError, setIndicatorError] = useState('');
    const [nouvelIndicateurId, setNouvelIndicateurId] = useState('');
    const [editId, setEditId] = useState(null);
    const [editDesc, setEditDesc] = useState('');
    const [editIndicateurId, setEditIndicateurId] = useState('');
    const [adding, setAdding] = useState(false);
    const causes = p?.causes || [];

    useEffect(() => {
        let active = true;
        if (!p?.id) return undefined;
        store.getCauseIndicatorTargets(p.id).then(({ targets, error }) => {
            if (!active) return;
            setIndicatorTargets(targets);
            setIndicatorError(error || '');
        });
        return () => { active = false; };
    }, [store, p?.id]);

    const handleAdd = async () => {
        if (!nouvelleDesc.trim()) return;
        setAdding(true);
        try {
            await store.addCause(p.id, {
                categorie: nouvelleCategorie,
                description: nouvelleDesc.trim(),
                indicator_widget_id: nouvelIndicateurId || null,
            });
            setNouvelleDesc('');
            setNouvelIndicateurId('');
        } finally {
            setAdding(false);
        }
    };

    const handleEdit = async (id) => {
        await store.updateCause(id, {
            description: editDesc,
            indicator_widget_id: editIndicateurId || null,
        });
        setEditId(null);
    };

    const causesByCategorie = CATEGORIES_6M.map(cat => ({
        ...cat,
        causes: causes.filter(c => c.categorie === cat.id),
    }));

    return (
        <div className={styles.etapeContent}>
            <div className={styles.etapeTitre}>
                <IcoAlert size={18} color="#FF9436"/>
                <span>Analyser les causes — Diagramme 6M</span>
            </div>

            {/* Formulaire ajout cause */}
            <div className={styles.addCauseForm}>
                <select
                    value={nouvelleCategorie}
                    onChange={e => setNouvelleCategorie(e.target.value)}
                    className={styles.selectCategorie}
                >
                    {CATEGORIES_6M.map(c => (
                        <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>
                    ))}
                </select>
                <input
                    type="text"
                    value={nouvelleDesc}
                    onChange={e => setNouvelleDesc(e.target.value)}
                    placeholder="Décrire la cause…"
                    className={styles.inputCause}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                />
                {indicatorTargets.length > 0 && <select
                    value={nouvelIndicateurId}
                    onChange={e => setNouvelIndicateurId(e.target.value)}
                    className={styles.selectIndicateur}
                    aria-label="Relier cette cause à un indicateur"
                >
                    <option value="">Aucun indicateur lié</option>
                    {indicatorTargets.map(indicator => <option key={indicator.id} value={indicator.id}>{indicator.name}</option>)}
                </select>}
                <button className={styles.btnAdd} onClick={handleAdd} disabled={adding || !nouvelleDesc.trim()}>
                    <IcoPlus/> Ajouter
                </button>
            </div>
            {indicatorError && <p className={styles.indicatorLinkError}>{indicatorError}</p>}
            {indicatorTargets.length > 0 && <p className={styles.indicatorLinkHint}>Facultatif : reliez la cause à un indicateur du Totem pour alimenter son Pareto et contextualiser ses actions Planning.</p>}

            {/* Grille 6M */}
            <div className={styles.grille6M}>
                {causesByCategorie.map(cat => (
                    <div key={cat.id} className={styles.bloc6M} style={{ borderTopColor: cat.color }}>
                        <div className={styles.bloc6MHeader} style={{ color: cat.color }}>
                            <span>{cat.emoji}</span>
                            <span>{cat.label}</span>
                            <span className={styles.bloc6MCount}>{cat.causes.length}</span>
                        </div>
                        <div className={styles.bloc6MCauses}>
                            {cat.causes.length === 0 && (
                                <span className={styles.bloc6MEmpty}>Aucune cause</span>
                            )}
                            {cat.causes.map(cause => (
                                <div key={cause.id} className={styles.causeItem}>
                                    {editId === cause.id ? (
                                        <>
                                        <input
                                            autoFocus
                                            value={editDesc}
                                            onChange={e => setEditDesc(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleEdit(cause.id)}
                                            className={styles.inputCauseEdit}
                                        />
                                        {indicatorTargets.length > 0 && <select
                                            value={editIndicateurId}
                                            onChange={e => setEditIndicateurId(e.target.value)}
                                            className={styles.selectIndicateurEdit}
                                            aria-label="Indicateur lié à cette cause"
                                        >
                                            <option value="">Aucun indicateur lié</option>
                                            {indicatorTargets.map(indicator => <option key={indicator.id} value={indicator.id}>{indicator.name}</option>)}
                                        </select>}
                                        <button type="button" className={styles.btnCauseSave} onClick={() => handleEdit(cause.id)}>Valider</button>
                                        </>
                                    ) : (
                                        <>
                                            <span className={styles.causeDesc}>
                                                {cause.description}
                                                {cause.indicator?.dis_nom_widget && <small className={styles.causeIndicatorLink}>↗ {cause.indicator.dis_nom_widget}</small>}
                                            </span>
                                            <div className={styles.causeActions}>
                                                <button onClick={() => {
                                                    setEditId(cause.id);
                                                    setEditDesc(cause.description);
                                                    setEditIndicateurId(cause.indicator_widget_id || '');
                                                }} className={styles.btnIcone}><IcoEdit size={11}/></button>
                                                <button onClick={() => store.deleteCause(cause.id)} className={styles.btnIcone}><IcoTrash size={11}/></button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.etapeFooter}>
                <button className={styles.btnBack} onClick={() => store.goToEtape(1)}>← Retour</button>
                <button className={styles.btnNext} onClick={() => store.advanceEtape(3)}>
                    Étape suivante <IcoArrow/>
                </button>
            </div>
        </div>
    );
});

// ── Étape 3 : Solutions ───────────────────────────────────────────────────────
const Etape3Solutions = observer(({ store }) => {
    const p = store.problemeActif;
    const [nouvelleTitre, setNouvelleTitre] = React.useState('');
    const [nouvelleDesc, setNouvelleDesc] = React.useState('');
    const [nouvelInitiateur, setNouvelInitiateur] = React.useState('');
    const [nouvelleCauseId, setNouvelleCauseId] = React.useState('');
    const [adding, setAdding] = React.useState(false);
    const solutions = p?.solutions || [];
    const causes = p?.causes || [];

    const handleAdd = async () => {
        if (!nouvelleTitre.trim()) return;
        setAdding(true);
        try {
            await store.addSolution(p.id, {
                titre: nouvelleTitre.trim(),
                description: nouvelleDesc.trim() || null,
                initiateur: nouvelInitiateur.trim() || null,
                cause_id: nouvelleCauseId ? Number(nouvelleCauseId) : null,
            });
            setNouvelleTitre('');
            setNouvelleDesc('');
            setNouvelInitiateur('');
            setNouvelleCauseId('');
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className={styles.etapeContent}>
            <div className={styles.etapeTitre}>
                <IcoBulb size={18} color="#66BE63"/>
                <span>Brainstorming — Proposer des idées de solutions</span>
            </div>
            <p style={{ fontSize: 12, color: '#70787B', margin: '0 0 12px', lineHeight: 1.5 }}>
                À ce stade, on collecte des <strong>idées</strong> sans notion de responsable ni de date.
                La priorisation se fait à l'étape suivante.
            </p>
            {/* Formulaire ajout idée */}
            <div className={styles.addSolutionForm} style={{ flexDirection: 'column', gap: 8 }}>
                    <input
                        type="text"
                        value={nouvelleTitre}
                    onChange={e => setNouvelleTitre(e.target.value)}
                    placeholder="Intitulé de l'idée *"
                    className={styles.inputSolution}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        style={{ width: '100%' }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                    <input
                        type="text"
                        value={nouvelleDesc}
                        onChange={e => setNouvelleDesc(e.target.value)}
                        placeholder="Description (optionnel)"
                        className={styles.inputResponsable}
                        style={{ flex: 2 }}
                    />
                        <input
                            type="text"
                            value={nouvelInitiateur}
                        onChange={e => setNouvelInitiateur(e.target.value)}
                        placeholder="Initiateur (optionnel)"
                            className={styles.inputResponsable}
                            style={{ flex: 1 }}
                        />
                        <select
                            value={nouvelleCauseId}
                            onChange={e => setNouvelleCauseId(e.target.value)}
                            className={styles.inputResponsable}
                            style={{ flex: 1, minWidth: 150 }}
                            aria-label="Cause 6M traitée par cette solution"
                        >
                            <option value="">Cause 6M associée (optionnel)</option>
                            {causes.map(cause => (
                                <option key={cause.id} value={cause.id}>
                                    {cause.categorie} — {cause.description}
                                </option>
                            ))}
                        </select>
                        <button className={styles.btnAdd} onClick={handleAdd} disabled={adding || !nouvelleTitre.trim()}>
                            <IcoPlus/> Ajouter
                        </button>
                    </div>
                </div>
                {causes.length === 0 && <p style={{ fontSize: 12, color: '#C66A00', margin: '8px 0 0' }}>Ajoutez une cause 6M à l’étape précédente pour pouvoir relier vos solutions au diagnostic.</p>}
            {/* Liste des idées */}
            <div className={styles.listeSolutions}>
                {solutions.length === 0 && (
                    <div className={styles.emptyState}>
                        <IcoBulb size={28} color="#DBE3E7"/>
                        <span>Aucune idée proposée pour l'instant</span>
                    </div>
                )}
                {solutions.map(sol => (
                    <div key={sol.id} className={styles.solutionItem}>
                        <div className={styles.solutionDesc} style={{ fontWeight: 600 }}>{sol.titre}</div>
                        {sol.description && <div style={{ fontSize: 12, color: '#70787B', marginTop: 2 }}>{sol.description}</div>}
                        {causes.length > 0 && <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#70787B', marginTop: 7 }}>
                            <span style={{ fontWeight: 700 }}>Cause 6M</span>
                            <select
                                value={sol.cause_id || ''}
                                onChange={event => store.updateSolution(sol.id, { cause_id: event.target.value ? Number(event.target.value) : null })}
                                style={{ flex: 1, maxWidth: 360, padding: '4px 7px', border: '1px solid #DBE3E7', borderRadius: 5, color: '#002335', background: '#fff', fontSize: 11 }}
                                aria-label={`Cause liée à la solution ${sol.titre}`}
                            >
                                <option value="">Aucune cause associée</option>
                                {causes.map(cause => (
                                    <option key={cause.id} value={cause.id}>
                                        {cause.categorie} — {cause.description}
                                    </option>
                                ))}
                            </select>
                        </label>}
                        {sol.initiateur && (
                            <div className={styles.solutionMeta}>
                                <span className={styles.solutionResponsable}>💡 {sol.initiateur}</span>
                            </div>
                        )}
                        <button onClick={() => store.deleteSolution(sol.id)} className={`${styles.btnIcone} ${styles.btnIconeFloat}`}><IcoTrash size={11}/></button>
                    </div>
                ))}
            </div>
            <div className={styles.etapeFooter}>
                <button className={styles.btnBack} onClick={() => store.goToEtape(2)}>← Retour</button>
                <button className={styles.btnNext} onClick={() => store.advanceEtape(4)} disabled={solutions.length === 0}>
                    Prioriser les idées <IcoArrow/>
                </button>
            </div>
        </div>
    );
});
// ── Étape 4 : Matrice Enjeu × Facilité ─────────────────────────────────────
const ENJEU_VALS  = ['faible', 'moyen', 'fort'];
const FACIL_VALS  = ['difficile', 'moyen', 'facile'];
const ENJEU_LABELS  = { faible: 'Faible', moyen: 'Moyen', fort: 'Fort' };
const FACIL_LABELS  = { difficile: 'Difficile', moyen: 'Moyen', facile: 'Facile' };
// Keep backward compat aliases used by Etape5 / sort
const IMPACT_VALS = ENJEU_VALS;
const FAISA_VALS  = FACIL_VALS;

// Couleur de priorité selon enjeu×facilité
const getPriorite = (enjeu, facilite) => {
    if (!enjeu || !facilite) return { label: '—', color: '#DBE3E7', bg: '#F4F7F9' };
    const score = (ENJEU_VALS.indexOf(enjeu) + 1) * (FACIL_VALS.indexOf(facilite) + 1);
    if (score >= 6) return { label: 'Priorité haute', color: '#4CAF50', bg: '#E8F5E9' };
    if (score >= 3) return { label: 'Faire si possible', color: '#FF9436', bg: '#FFF3E0' };
    if (score === 2) return { label: 'Idée à creuser', color: '#00B0FF', bg: '#E3F2FD' };
    return { label: 'Déprioritiser', color: '#B0BEC5', bg: '#F4F7F9' };
};

// Couleur de fond de chaque case de la matrice
const getCellBg = (enjeu, facilite) => {
    const score = (ENJEU_VALS.indexOf(enjeu) + 1) * (FACIL_VALS.indexOf(facilite) + 1);
    if (score >= 6) return '#E8F5E9';
    if (score >= 3) return '#FFF3E0';
    if (score === 2) return '#E3F2FD';
    return '#F4F7F9';
};

const ModalCreerAction = ({ solution, causes, store, onClose, onGoToCauses }) => {
    const [titre, setTitre] = React.useState(solution.titre || '');
    const [echeance, setEcheance] = React.useState('');
    const [totemId, setTotemId] = React.useState('');
    const [causeId, setCauseId] = React.useState(solution.cause_id ? String(solution.cause_id) : '');
    const [totems, setTotems] = React.useState([]);
    const [targetsError, setTargetsError] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [sent, setSent] = React.useState(false);
    const [error, setError] = React.useState('');
    React.useEffect(() => {
        store.getIntertotemTargets(solution.id).then(({ targets, error: targetError }) => {
            setTotems(targets);
            setTargetsError(targetError || '');
            if (targets.length > 0) setTotemId(targets[0].id_totem || '');
        });
        if (causes.length > 0) {
            const linkedCauseExists = causes.some(cause => String(cause.id) === String(solution.cause_id));
            setCauseId(linkedCauseExists ? String(solution.cause_id) : String(causes[0].id));
        }
    }, [store, solution.id, solution.cause_id, causes]);
    const handleSave = async () => {
        if (!titre.trim() || !totemId || !causeId) return;
        setSaving(true);
        setError('');
        try {
            await store.creerActionDepuisSolution(solution.id, {
                destination_totem_id: totemId,
                cause_id: Number(causeId),
                titre: titre.trim(),
                echeance: echeance || null,
            });
            setSent(true);
            setTimeout(() => onClose(true), 1800);
        } catch (err) {
            setError(err.response?.data?.message || 'L’action n’a pas pu être envoyée.');
            setSaving(false);
        }
    };
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,35,53,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,35,53,0.18)' }}>
                {sent ? (
                    /* ── Accusé de réception ── */
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '12px 0' }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                                <path d="M5 14L11 20L23 8" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#002335', textAlign: 'center' }}>Action envoyée !</p>
                        <p style={{ margin: 0, fontSize: 12, color: '#70787B', textAlign: 'center' }}>L’action est dans les Actions reçues du Totem destinataire.</p>
                    </div>
                ) : (
                    <>
                        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#002335' }}>Envoyer une action à un Totem</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#70787B', display: 'block', marginBottom: 4 }}>Intitulé de l'action *</label>
                                <input value={titre} onChange={e => setTitre(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #DBE3E7', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#70787B', display: 'block', marginBottom: 4 }}>Délai (optionnel)</label>
                                <input type="date" value={echeance} onChange={e => setEcheance(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #DBE3E7', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#70787B', display: 'block', marginBottom: 4 }}>Totem destinataire *</label>
                                {totems.length === 0 ? (
                                    <p style={{ fontSize: 12, color: targetsError ? '#C62828' : '#B0BEC5' }}>{targetsError || 'Aucun Totem n’est rattaché à cet espace.'}</p>
                                ) : (
                                    <select value={totemId} onChange={e => setTotemId(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #DBE3E7', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }}>
                                        {totems.map(totem => (
                                            <option key={totem.id_totem} value={totem.id_totem}>
                                                {totem.tot_nom}{totem.is_source_totem ? ' — mon Totem' : ''}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#70787B', display: 'block', marginBottom: 4 }}>Cause liée à la solution *</label>
                                {causes.length === 0 ? <p style={{ fontSize: 12, color: '#C66A00', lineHeight: 1.5, margin: 0 }}>Une cause est nécessaire pour transmettre l’action avec son contexte RSP.</p> : (
                                    <select value={causeId} onChange={e => setCauseId(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #DBE3E7', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }}>
                                        {causes.map(cause => <option key={cause.id} value={cause.id}>{cause.categorie} — {cause.description}</option>)}
                                    </select>
                                )}
                            </div>
                        </div>
                        {error && <p style={{ color: '#C62828', fontSize: 12, margin: '10px 0 0' }}>{error}</p>}
                        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                            <button onClick={() => onClose(false)} style={{ padding: '8px 18px', border: '1.5px solid #DBE3E7', borderRadius: 7, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#70787B' }}>Annuler</button>
                            <button onClick={causes.length === 0 ? onGoToCauses : handleSave} disabled={saving || !titre.trim() || !totemId} style={{ padding: '8px 18px', background: '#00B0FF', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                                {causes.length === 0 ? 'Ajouter une cause' : saving ? 'Envoi…' : '→ Envoyer au Totem'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/* ── Modal Envoyer Tout ─────────────────────────────────────────────────────── */
const ModalEnvoyerTout = ({ solutions, causes, store, onClose, onGoToCauses }) => {
    const [totemId, setTotemId] = React.useState('');
    const [totems, setTotems] = React.useState([]);
    const [targetsError, setTargetsError] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [sent, setSent] = React.useState(false);
    const [sentCount, setSentCount] = React.useState(0);
    const [failedCount, setFailedCount] = React.useState(0);
    const [error, setError] = React.useState('');
    React.useEffect(() => {
        const referenceSolution = solutions[0];
        if (!referenceSolution) return;
        store.getIntertotemTargets(referenceSolution.id).then(({ targets, error: targetError }) => {
            setTotems(targets);
            setTargetsError(targetError || '');
            if (targets.length > 0) setTotemId(targets[0].id_totem || '');
        });
    }, [store, solutions, causes]);
    const solutionsSansCause = solutions.filter(solution => !solution.cause_id);
    const handleEnvoyerTout = async () => {
        if (!totemId || solutionsSansCause.length > 0) return;
        setSaving(true);
        setError('');
        let count = 0;
        let failures = 0;
        for (const sol of solutions) {
            try {
                await store.creerActionDepuisSolution(sol.id, {
                    destination_totem_id: totemId,
                    cause_id: sol.cause_id,
                    titre: sol.titre,
                    echeance: null,
                });
                count++;
            } catch (_) {
                failures++;
            }
        }
        setSentCount(count);
        setFailedCount(failures);
        if (failures > 0) setError(`${failures} action${failures > 1 ? 's' : ''} n’a pas pu être envoyée.`);
        setSent(true);
        setTimeout(() => onClose(count > 0), 2200);
    };
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,35,53,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,35,53,0.18)' }}>
                {sent ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '12px 0' }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                                <path d="M5 14L11 20L23 8" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#002335', textAlign: 'center' }}>
                            {sentCount} action{sentCount > 1 ? 's' : ''} envoyée{sentCount > 1 ? 's' : ''} !
                        </p>
                        <p style={{ margin: 0, fontSize: 12, color: '#70787B', textAlign: 'center' }}>
                            {failedCount === 0
                                ? 'Toutes les actions ont été envoyées au Totem et sont traçables dans ses Actions reçues.'
                                : `${failedCount} action${failedCount > 1 ? 's' : ''} n’a pas pu être envoyée. Réessayez ou contactez votre administrateur.`}
                        </p>
                    </div>
                ) : (
                    <>
                        <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#002335' }}>Envoyer tout le plan d'actions</h3>
                        <p style={{ margin: '0 0 16px', fontSize: 12, color: '#70787B' }}>
                            {solutions.length} solution{solutions.length > 1 ? 's' : ''} seront adressées au Totem sélectionné. Chacune conservera la cause 6M qui lui est associée.
                        </p>
                        <div style={{ background: '#F4F7F9', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
                            {solutions.map((sol, i) => (
                                <div key={sol.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: i < solutions.length - 1 ? '1px solid #DBE3E7' : 'none' }}>
                                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#002335', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
                                    <span style={{ fontSize: 12, color: '#002335', fontWeight: 600, flex: 1 }}>{sol.titre}</span>
                                    <span style={{ fontSize: 10, color: sol.cause_id ? '#4CAF50' : '#C66A00', fontWeight: 700 }}>{sol.cause_id ? 'Cause liée' : 'Cause à lier'}</span>
                                </div>
                            ))}
                        </div>
                        <div>
                            <label style={{ fontSize: 12, fontWeight: 600, color: '#70787B', display: 'block', marginBottom: 4 }}>Totem destinataire *</label>
                            {totems.length === 0 ? (
                                <p style={{ fontSize: 12, color: targetsError ? '#C62828' : '#B0BEC5' }}>{targetsError || 'Aucun Totem n’est rattaché à cet espace.'}</p>
                            ) : (
                                <select value={totemId} onChange={e => setTotemId(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #DBE3E7', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }}>
                                    {totems.map(totem => (
                                        <option key={totem.id_totem} value={totem.id_totem}>
                                            {totem.tot_nom}{totem.is_source_totem ? ' — mon Totem' : ''}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        {solutionsSansCause.length > 0 && <p style={{ fontSize: 12, color: '#C66A00', lineHeight: 1.5, margin: '10px 0 0' }}>Associez une cause 6M à chaque solution à l’étape « Solutions » avant l’envoi groupé.</p>}
                        {error && <p style={{ color: '#C62828', fontSize: 12, margin: '10px 0 0' }}>{error}</p>}
                        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                            <button onClick={() => onClose(false)} style={{ padding: '8px 18px', border: '1.5px solid #DBE3E7', borderRadius: 7, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#70787B' }}>Annuler</button>
                            <button onClick={solutionsSansCause.length > 0 ? onGoToCauses : handleEnvoyerTout} disabled={saving || !totemId} style={{ padding: '8px 18px', background: '#002335', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                                {solutionsSansCause.length > 0 ? 'Lier les causes' : saving ? 'Envoi…' : `→ Envoyer ${solutions.length} action${solutions.length > 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const Etape4Matrice = observer(({ store }) => {
    const p = store.problemeActif;
    const solutions = p?.solutions || [];
    const [modalSol, setModalSol] = React.useState(null);
    const [successMsg, setSuccessMsg] = React.useState('');
    const [solutionSelectionneeId, setSolutionSelectionneeId] = React.useState(null);

    const solutionSelectionnee = solutions.find(solution => solution.id === solutionSelectionneeId)
        || solutions.find(solution => !solution.impact || !solution.faisabilite)
        || solutions[0];

    const handleCellClick = async (enjeu, facilite) => {
        if (!solutionSelectionnee) return;
        const newEnjeu    = solutionSelectionnee.impact      === enjeu    ? null : enjeu;
        const newFacilite = solutionSelectionnee.faisabilite === facilite ? null : facilite;
        await store.updateSolution(solutionSelectionnee.id, { impact: newEnjeu, faisabilite: newFacilite });
    };

    const handleCreerAction = (sol) => setModalSol(sol);
    const handleModalClose = (success) => {
        setModalSol(null);
        if (success) {
            setSuccessMsg("Action envoyée au Totem destinataire avec son contexte RSP.");
            setTimeout(() => setSuccessMsg(''), 4000);
        }
    };
    const [modalEnvoyerTout, setModalEnvoyerTout] = React.useState(false);
    const handleEnvoyerToutClose = (success) => {
        setModalEnvoyerTout(false);
        if (success) {
            setSuccessMsg("Les actions ont été envoyées aux Actions reçues du Totem destinataire.");
            setTimeout(() => setSuccessMsg(''), 4000);
        }
    };
    const handleGoToCauses = () => {
        setModalSol(null);
        setModalEnvoyerTout(false);
        store.goToEtape(2);
    };
    const handleGoToSolutions = () => {
        setModalSol(null);
        setModalEnvoyerTout(false);
        store.goToEtape(3);
    };
    // Solutions positionnées dans la matrice (enjeu + facilité définis)
    const solutionsPlacees = solutions.filter(s => s.impact && s.faisabilite);

    // Sorted list for the bottom panel
    const sorted = [...solutions].sort((a, b) => {
        const scoreA = (ENJEU_VALS.indexOf(a.impact) + 1) * (FACIL_VALS.indexOf(a.faisabilite) + 1);
        const scoreB = (ENJEU_VALS.indexOf(b.impact) + 1) * (FACIL_VALS.indexOf(b.faisabilite) + 1);
        return scoreB - scoreA;
    });

    // ENJEU rows: fort (top) → faible (bottom)
    const enjeuRows = ['fort', 'moyen', 'faible'];
    // FACILITÉ cols: difficile (left) → facile (right)
    const facilCols = ['difficile', 'moyen', 'facile'];

    const CELL_SIZE = 110; // px per cell
    const LABEL_W   = 72;  // px for Y-axis label column
    const LABEL_H   = 28;  // px for X-axis label row

    return (
        <div className={styles.etapeContent}>
            <div className={styles.etapeTitre}>
                <IcoCheck size={18} color="#9B59B6"/>
                <span>Matrice Enjeu × Facilité — Prioriser les idées</span>
            </div>

            {successMsg && (
                <div style={{ background: '#E8F5E9', color: '#4CAF50', padding: '8px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
                    ✓ {successMsg}
                </div>
            )}

            {solutions.length === 0 ? (
                <div className={styles.emptyState}>
                    <span>Ajoutez des idées à l'étape précédente</span>
                </div>
            ) : (
                <>
                    {/* ── Sélecteur de solution à positionner ── */}
                    <div style={{ marginBottom: 12 }}>
                        <p style={{ fontSize: 12, color: '#70787B', marginBottom: 6, fontWeight: 600 }}>
                            Sélectionnez une solution, puis cliquez sur une case pour définir ou modifier sa priorité :
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {solutions.map((sol, idx) => {
                                const prio = getPriorite(sol.impact, sol.faisabilite);
                                const placed = sol.impact && sol.faisabilite;
                                const isSelected = solutionSelectionnee?.id === sol.id;
                                return (
                                    <button type="button" key={sol.id} onClick={() => setSolutionSelectionneeId(sol.id)} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        padding: '3px 10px', borderRadius: 20,
                                        fontSize: 11, fontWeight: 700,
                                        background: placed ? prio.bg : '#F4F7F9',
                                        color: placed ? prio.color : '#A8B0B3',
                                        border: `2px solid ${isSelected ? '#002335' : placed ? prio.color : '#DBE3E7'}`,
                                        cursor: 'pointer',
                                        boxShadow: isSelected ? '0 0 0 2px rgba(0,176,255,0.18)' : 'none',
                                    }}>
                                        <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: '50%', background: '#002335', color: '#fff', textAlign: 'center', lineHeight: '16px', fontSize: 9, fontWeight: 800, flexShrink: 0 }}>{idx + 1}</span>
                                        {sol.titre.length > 22 ? sol.titre.slice(0, 22) + '…' : sol.titre}
                                        {placed && <span style={{ fontSize: 10 }}>✓</span>}
                                    </button>
                                );
                            })}
                        </div>
                        {solutionSelectionnee && <p style={{ fontSize: 11, color: '#00B0FF', margin: '7px 0 0', fontWeight: 700 }}>Solution sélectionnée : {solutionSelectionnee.titre}</p>}
                    </div>

                    {/* ── Matrice visuelle 3×3 ── */}
                    <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                        <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: LABEL_W + CELL_SIZE * 3 + 2 }}>

                            {/* Titre axe Y (vertical) + grille */}
                            <div style={{ display: 'flex', alignItems: 'stretch' }}>

                                {/* Axe Y label + lignes */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: LABEL_W, flexShrink: 0 }}>
                                    <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 11, fontWeight: 700, color: '#002335', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                                        Enjeu →
                                    </div>
                                </div>

                                {/* Grille principale */}
                                <div style={{ flex: 1 }}>
                                    {/* En-tête colonnes (Facilité) */}
                                    <div style={{ display: 'flex', marginBottom: 2 }}>
                                        {facilCols.map(f => (
                                            <div key={f} style={{ width: CELL_SIZE, textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#70787B', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 4px', flexShrink: 0 }}>
                                                {FACIL_LABELS[f]}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Lignes de la matrice */}
                                    {enjeuRows.map((enjeu) => (
                                        <div key={enjeu} style={{ display: 'flex', marginBottom: 2 }}>
                                            {facilCols.map((facilite) => {
                                                const bg = getCellBg(enjeu, facilite);
                                                const prio = getPriorite(enjeu, facilite);
                                                // Solutions placées dans cette case
                                                const inCell = solutions.filter(s => s.impact === enjeu && s.faisabilite === facilite);
                                                return (
                                                    <div key={facilite} style={{
                                                        width: CELL_SIZE, minHeight: CELL_SIZE, flexShrink: 0,
                                                        background: bg, border: '1.5px solid #DBE3E7',
                                                        borderRadius: 8, padding: 6,
                                                        display: 'flex', flexDirection: 'column', gap: 4,
                                                        position: 'relative', cursor: 'pointer',
                                                        transition: 'box-shadow 0.15s',
                                                    }}
                                                        onClick={() => handleCellClick(enjeu, facilite)}
                                                        title={`${ENJEU_LABELS[enjeu]} / ${FACIL_LABELS[facilite]} — ${prio.label}`}
                                                    >
                                                        {/* Label quadrant (coin haut-gauche, discret) */}
                                                        <span style={{ fontSize: 9, fontWeight: 700, color: prio.color, textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.2 }}>
                                                            {prio.label}
                                                        </span>
                                                        {/* Pastilles des solutions */}
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, flex: 1, alignContent: 'flex-start' }}>
                                                            {inCell.map((sol, idx2) => {
                                                                const globalIdx = solutions.indexOf(sol);
                                                                return (
                                                                    <div key={sol.id}
                                                                        onClick={e => { e.stopPropagation(); store.updateSolution(sol.id, { impact: null, faisabilite: null }); }}
                                                                        title={`${sol.titre} — cliquer pour retirer`}
                                                                        style={{
                                                                            width: 22, height: 22, borderRadius: '50%',
                                                                            background: '#002335', color: '#fff',
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            fontSize: 9, fontWeight: 800, cursor: 'pointer',
                                                                            flexShrink: 0, border: '2px solid #fff',
                                                                            boxShadow: '0 1px 4px rgba(0,35,53,0.2)',
                                                                        }}>
                                                                        {globalIdx + 1}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}

                                    {/* Étiquettes lignes (Enjeu) à droite */}
                                </div>

                                {/* Labels lignes Y à droite */}
                                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', paddingLeft: 6, gap: 2 }}>
                                    {enjeuRows.map(e => (
                                        <div key={e} style={{ height: CELL_SIZE + 2, display: 'flex', alignItems: 'center' }}>
                                            <span style={{ fontSize: 10, fontWeight: 700, color: '#70787B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                {ENJEU_LABELS[e]}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Axe X label (Facilité) */}
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4, paddingLeft: LABEL_W }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#002335', letterSpacing: 1, textTransform: 'uppercase' }}>
                                    ← Facilité →
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── Liste des solutions avec bouton Créer une action ── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {sorted.map((sol) => {
                            const prio = getPriorite(sol.impact, sol.faisabilite);
                            const globalIdx = solutions.indexOf(sol);
                            return (
                                <div key={sol.id} style={{ background: '#fff', border: '1.5px solid #DBE3E7', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#002335', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                                        {globalIdx + 1}
                                    </div>
                                    <span style={{ fontWeight: 700, color: '#002335', fontSize: 13, flex: 1 }}>{sol.titre}</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: prio.bg, color: prio.color, flexShrink: 0 }}>{prio.label}</span>
                                    {sol.statut === 'validee' && <span title="L’avancement se met à jour dans le Planning et la Boîte d’échange." style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: '#E8F7FF', color: '#0077B6', flexShrink: 0 }}>Suivi Planning</span>}
                                    <button onClick={() => handleCreerAction(sol)}
                                        style={{ padding: '5px 12px', background: '#00B0FF', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                        <IcoArrow/> {sol.statut === 'validee' ? 'Renvoyer' : 'Action'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

                    {/* ── Bouton Envoyer toutes les solutions à un Totem ── */}
            {solutionsPlacees.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 4 }}>
                    <button onClick={() => setModalEnvoyerTout(true)}
                        style={{ padding: '8px 18px', background: '#002335', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8L14 2L8 14L7 9L2 8Z" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                        Envoyer {solutionsPlacees.length} action{solutionsPlacees.length > 1 ? 's' : ''} à un Totem
                    </button>
                </div>
            )}
            <div className={styles.etapeFooter}>
                <button className={styles.btnBack} onClick={() => store.goToEtape(3)}>← Retour</button>
                <button className={styles.btnNext} onClick={() => store.advanceEtape(5)}>
                    Étape suivante <IcoArrow/>
                </button>
            </div>
            {modalSol && <ModalCreerAction solution={modalSol} causes={p?.causes || []} store={store} onClose={handleModalClose} onGoToCauses={handleGoToCauses} />}
            {modalEnvoyerTout && <ModalEnvoyerTout solutions={solutionsPlacees} causes={p?.causes || []} store={store} onClose={handleEnvoyerToutClose} onGoToCauses={handleGoToSolutions} />}
        </div>
    );
});
// ── Étape 5
// ── Étape 5 : Validation / Clôture ───────────────────────────────────────────
const Etape5Validation = observer(({ store }) => {
    const p = store.problemeActif;
    const [bilan, setBilan] = useState(p?.bilan || '');
    const [saving, setSaving] = useState(false);

    const handleResoudre = async () => {
        setSaving(true);
        try {
            await store.updateProbleme(p.id, { bilan, statut: 'resolu', etape_courante: 5 });
            await store.marquerResolu();
        } finally {
            setSaving(false);
        }
    };

    const solutions = p?.solutions || [];
    const solutionTop = [...solutions].sort((a, b) => {
        const totA = Object.values(a.scores || {}).reduce((s, v) => s + v, 0);
        const totB = Object.values(b.scores || {}).reduce((s, v) => s + v, 0);
        return totB - totA;
    })[0];

    return (
        <div className={styles.etapeContent}>
            <div className={styles.etapeTitre}>
                <IcoCheck size={18} color="#66BE63"/>
                <span>Valider et clôturer</span>
            </div>

            {/* Récap */}
            <div className={styles.recapCard}>
                <div className={styles.recapRow}><span>Problème</span><strong>{p?.titre}</strong></div>
                <div className={styles.recapRow}><span>Causes identifiées</span><strong>{p?.causes?.length || 0}</strong></div>
                <div className={styles.recapRow}><span>Solutions proposées</span><strong>{solutions.length}</strong></div>
                {solutionTop && (
                    <div className={styles.recapRow}>
                        <span>Solution retenue</span>
                        <strong className={styles.recapSolutionTop}>⭐ {solutionTop.description}</strong>
                    </div>
                )}
            </div>

            {/* Bilan */}
            <div className={styles.bilanSection}>
                <label>Bilan / Commentaire de clôture</label>
                <textarea
                    value={bilan}
                    onChange={e => setBilan(e.target.value)}
                    placeholder="Décrivez le résultat obtenu, les leçons apprises…"
                    rows={4}
                    className={styles.textareaBilan}
                />
            </div>

            <div className={styles.etapeFooter}>
                <button className={styles.btnBack} onClick={() => store.goToEtape(4)}>← Retour</button>
                <button className={styles.btnResoudre} onClick={handleResoudre} disabled={saving}>
                    {saving ? 'Clôture en cours…' : '✓ Marquer comme résolu'}
                </button>
            </div>
        </div>
    );
});

// ── Vue Plein Écran (RSP ouvert) ──────────────────────────────────────────────
const RspPleinEcran = observer(({ store }) => {
    const p = store.problemeActif;
    const etape = p?.etape_courante || 1;

    useEffect(() => {
        if (p) store.fetchProbleme(p.id);
    }, [p?.id]);

    if (store.isLoading) {
        return (
            <div className={styles.loading}>
                <div className={styles.spinner}/>
                <span>Chargement…</span>
            </div>
        );
    }

    if (!p) return null;

    const renderEtape = () => {
        const v = store.view;
        if (v === 'etape1') return <Etape1QQOQCCP store={store}/>;
        if (v === 'etape2') return <Etape2Causes store={store}/>;
        if (v === 'etape3') return <Etape3Solutions store={store}/>;
        if (v === 'etape4') return <Etape4Matrice store={store}/>;
        if (v === 'etape5') return <Etape5Validation store={store}/>;
        return <Etape1QQOQCCP store={store}/>;
    };

    return (
        <div className={styles.pleinEcran}>
            {/* Header */}
            <div className={styles.pleinEcranHeader}>
                <button className={styles.btnRetourListe} onClick={() => store.goToListe()}>
                    ← Retour
                </button>
                <div className={styles.pleinEcranTitre}>{p.titre}</div>
                <span className={styles.pleinEcranStatut} style={{
                    color: STATUTS[p.statut]?.color,
                    background: STATUTS[p.statut]?.bg,
                }}>
                    {STATUTS[p.statut]?.label}
                </span>
            </div>

            {/* Barre d'étapes */}
            <BarreEtapes etapeCourante={etape} onClickEtape={n => store.setView(`etape${n}`)}/>

            {/* Contenu de l'étape */}
            {renderEtape()}
        </div>
    );
});

// ── Vue Liste des problèmes ───────────────────────────────────────────────────
const RspListe = observer(({ store }) => {
    const [showForm, setShowForm] = useState(false);
    const [titre, setTitre] = useState('');
    const [description, setDescription] = useState('');
    const [creating, setCreating] = useState(false);
    const [filtre, setFiltre] = useState('tous');

    const handleCreate = async () => {
        if (!titre.trim()) return;
        setCreating(true);
        try {
            await store.createProbleme({ titre: titre.trim(), description: description.trim() });
            setTitre('');
            setDescription('');
            setShowForm(false);
        } finally {
            setCreating(false);
        }
    };

    const problemesFiltrés = store.problemes.filter(p => {
        if (filtre === 'tous') return true;
        return p.statut === filtre;
    });

    const kpis = {
        total: store.problemes.length,
        ouvert: store.problemes.filter(p => p.statut === 'ouvert').length,
        en_cours: store.problemes.filter(p => p.statut === 'en_cours').length,
        resolu: store.problemes.filter(p => p.statut === 'resolu').length,
    };

    return (
        <div className={styles.listePage}>
            {/* KPIs */}
            <div className={styles.kpiRow}>
                {[
                    { key: 'total',    label: 'Total',    color: '#002335', bg: '#F4F7F9' },
                    { key: 'ouvert',   label: 'Ouverts',  color: '#00B0FF', bg: '#E8F7FF' },
                    { key: 'en_cours', label: 'En cours', color: '#FF9436', bg: '#FFF4E8' },
                    { key: 'resolu',   label: 'Résolus',  color: '#66BE63', bg: '#EBF7EB' },
                ].map(k => (
                    <button
                        key={k.key}
                        className={`${styles.kpiCard} ${filtre === k.key ? styles.kpiCardActive : ''}`}
                        style={{ '--kpi-color': k.color, '--kpi-bg': k.bg }}
                        onClick={() => setFiltre(filtre === k.key ? 'tous' : k.key)}
                    >
                        <span className={styles.kpiVal}>{kpis[k.key]}</span>
                        <span className={styles.kpiLabel}>{k.label}</span>
                    </button>
                ))}
            </div>

            {/* Bouton nouveau problème */}
            <button className={styles.btnNouveauProbleme} onClick={() => setShowForm(!showForm)}>
                <IcoPlus size={14}/> Nouveau problème
            </button>

            {/* Formulaire création */}
            {showForm && (
                <div className={styles.formNouveauProbleme}>
                    <input
                        autoFocus
                        type="text"
                        value={titre}
                        onChange={e => setTitre(e.target.value)}
                        placeholder="Titre du problème…"
                        className={styles.inputTitre}
                        onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    />
                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Description (optionnel)…"
                        rows={2}
                        className={styles.textareaDesc}
                    />
                    <div className={styles.formActions}>
                        <button className={styles.btnAnnuler} onClick={() => setShowForm(false)}>Annuler</button>
                        <button className={styles.btnCreer} onClick={handleCreate} disabled={creating || !titre.trim()}>
                            {creating ? 'Création…' : 'Créer'}
                        </button>
                    </div>
                </div>
            )}

            {/* Liste */}
            {store.isLoading ? (
                <div className={styles.loading}><div className={styles.spinner}/><span>Chargement…</span></div>
            ) : problemesFiltrés.length === 0 ? (
                <div className={styles.emptyState}>
                    <IcoBulb size={32} color="#DBE3E7"/>
                    <span>Aucun problème {filtre !== 'tous' ? `"${STATUTS[filtre]?.label}"` : ''}</span>
                    <span className={styles.emptyHint}>Créez votre premier problème ci-dessus</span>
                </div>
            ) : (
                <div className={styles.listeProblemes}>
                    {problemesFiltrés.map(p => (
                        <CarteProbleme
                            key={p.id}
                            probleme={p}
                            onClick={() => store.selectProbleme(p.id)}
                            onDelete={id => store.deleteProbleme(id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
});

// ── Composant principal WidgetRSP (vue carte dashboard) ───────────────────────
const WidgetRSP = observer(({ idWidget }) => {
    const { uiStore, RspStore, widgetStore } = useStores();
    const { ent } = useParams();
    const containerRef = useRef(null);
    const [bp, setBp] = useState('md');
    const [isLoading, setIsLoading] = useState(true);
    const [nomRsp, setNomRsp] = useState('Résolution de Problèmes');
    const [showDisplaySettings, setShowDisplaySettings] = useState(false);
    const storageKey = `rsp-widget-cfg-${idWidget}`;
    const axiosInst = useMemo(() => createAxiosEntrepriseInstance(ent), [ent]);
    const [displayPreferences, setDisplayPreferences] = useState(() => {
        return readDisplayPreferences(storageKey, RSP_DEFAULT_DISPLAY);
    });

    useEffect(() => {
        let isActive = true;
        const localPreferences = readDisplayPreferences(storageKey, RSP_DEFAULT_DISPLAY);
        setDisplayPreferences(localPreferences);

        axiosInst
            .get(`/api/widget-display-preferences/${idWidget}`)
            .then(({data}) => {
                const remotePreferences = data?.data?.preferences;
                const preferences = remotePreferences && Object.keys(remotePreferences).length > 0
                    ? {...RSP_DEFAULT_DISPLAY, ...remotePreferences}
                    : localPreferences;

                if (!isActive) return;
                const normalized = saveDisplayPreferences(storageKey, preferences);
                setDisplayPreferences(normalized);

                if (!remotePreferences || Object.keys(remotePreferences).length === 0) {
                    return axiosInst.put(`/api/widget-display-preferences/${idWidget}`, {preferences: normalized});
                }
                return undefined;
            })
            .catch(() => undefined);

        return () => { isActive = false; };
    }, [axiosInst, idWidget, storageKey]);

    const savePreferences = useCallback((preferences) => {
        const normalized = saveDisplayPreferences(storageKey, {...RSP_DEFAULT_DISPLAY, ...preferences});
        setDisplayPreferences(normalized);
        void axiosInst.put(`/api/widget-display-preferences/${idWidget}`, {preferences: normalized}).catch(() => undefined);
    }, [axiosInst, idWidget, storageKey]);

    // ResizeObserver
    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(entries => {
            const { width, height } = entries[0].contentRect;
            setBp(getBp(width, height));
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    // Init store
    useEffect(() => {
        const token = widgetStore.getCookie('current_apiToken');
        RspStore.initialize(token, idWidget);
        RspStore.fetchProblemes().then(() => setIsLoading(false));
    }, [idWidget]);
    // Refresh quand le plein ecran RSP se ferme
    useEffect(() => {
        const dispose = reaction(
            () => uiStore.isConsultingModeRSP,
            (isOpen) => { if (!isOpen) RspStore.fetchProblemes(); }
        );
        return () => dispose();
    }, [uiStore, RspStore]);
    // Polling fallback 30s
    useEffect(() => {
        const interval = setInterval(() => RspStore.fetchProblemes(), 30000);
        return () => clearInterval(interval);
    }, [RspStore]);
    // Refresh sur visibilitychange
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') RspStore.fetchProblemes(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [RspStore]);
    const handleOpen = (e) => {
        e.stopPropagation();
        const token = widgetStore.getCookie('current_apiToken');
        uiStore.openConsultingModeRSP(RspStore, token, idWidget);
    };

    const handleQuickCreate = (event) => {
        event.stopPropagation();
        const token = widgetStore.getCookie('current_apiToken');
        RspStore.ouvrirFormCreation();
        uiStore.openConsultingModeRSP(RspStore, token, idWidget);
    };

    const kpis = {
        total:    RspStore.problemes.length,
        en_cours: RspStore.problemes.filter(p => p.statut === 'en_cours').length,
        resolu:   RspStore.problemes.filter(p => p.statut === 'resolu').length,
    };

    if (isLoading) {
        return (
            <div ref={containerRef} className={`carte_widget ${styles.container}`}>
                <div className={styles.loadingPulse}/>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={`carte_widget ${styles.container} ${styles[`bp_${bp}`]}`} onClick={handleOpen}>
            {/* Header */}
            <div className={styles.widgetHeader}>
                <div className={styles.widgetHeaderLeft}>
                    <div className={styles.widgetIcone}>
                        <IcoBulb size={14} color="#00B0FF"/>
                    </div>
                    {bp !== 'xs' && <span className={styles.widgetType}>RSP</span>}
                </div>
                {bp !== 'xs' && (
                    <div className={styles.widgetNom}>{nomRsp}</div>
                )}
                <button
                    type="button"
                    className={styles.btnSettings}
                    onClick={e => { e.stopPropagation(); setShowDisplaySettings(true); }}
                    title="Paramétrer l’affichage"
                    aria-label="Paramétrer l’affichage du phidget Résolution de problèmes"
                >
                    <IcoSettings/>
                </button>
            </div>

            {/* KPIs */}
            {bp !== 'xs' && displayPreferences.showKpis && (
                <div className={styles.kpisRow}>
                    <div className={styles.kpiItem}>
                        <span className={styles.kpiItemVal}>{kpis.total}</span>
                        <span className={styles.kpiItemLabel}>Problèmes</span>
                    </div>
                    <div className={`${styles.kpiItem} ${styles.kpiItemOrange}`}>
                        <span className={styles.kpiItemVal}>{kpis.en_cours}</span>
                        <span className={styles.kpiItemLabel}>En cours</span>
                    </div>
                    <div className={`${styles.kpiItem} ${styles.kpiItemVert}`}>
                        <span className={styles.kpiItemVal}>{kpis.resolu}</span>
                        <span className={styles.kpiItemLabel}>Résolus</span>
                    </div>
                </div>
            )}

            {/* Derniers problèmes ouverts */}
            {bp === 'lg' && displayPreferences.showRecentProblems && RspStore.problemes.filter(p => p.statut !== 'resolu').length > 0 && (
                <div className={styles.recentList}>
                    {RspStore.problemes.filter(p => p.statut !== 'resolu').slice(0,3).map(p => (
                        <div key={p.id} className={styles.recentItem}>
                            <span className={styles.recentDot} style={{ background: STATUTS[p.statut]?.color || '#A8B0B3' }}/>
                            <span className={styles.recentTitre}>{p.titre}</span>
                            <span className={styles.recentStatut} style={{ color: STATUTS[p.statut]?.color }}>{STATUTS[p.statut]?.label}</span>
                        </div>
                    ))}
                </div>
            )}
            {/* CTA */}
            {bp === 'lg' && displayPreferences.showOpenCta && (
                <div className={styles.ctaRow}>
                    <span className={styles.ctaLabel}>Ouvrir le module</span>
                    <IcoArrow/>
                </div>
            )}
            {bp !== 'xs' && (
                <button
                    type="button"
                    className={styles.quickCreate}
                    onClick={handleQuickCreate}
                    aria-label="Créer un nouveau problème"
                >
                    <IcoPlus size={14}/>
                    <span>Nouveau problème</span>
                </button>
            )}
            {showDisplaySettings && (
                <RspDisplaySettings
                    config={displayPreferences}
                    onSave={savePreferences}
                    onClose={() => setShowDisplaySettings(false)}
                />
            )}
        </div>
    );
});

export { Etape1QQOQCCP, Etape2Causes, Etape3Solutions, Etape4Matrice, Etape5Validation };
export default WidgetRSP;
